/**
 * 🐑 **The animal spine — T226, D117.**
 *
 * > ## **An animal is a THING THE SERVER KEEPS. It is never a formula.**
 *
 * 🎯 **haugur, 2026-09-17, and one line settled the whole architecture:**
 * *"yes animals should notice you, thats important."*
 *
 * 🔴 **A creature that notices you cannot be derived, because the player's
 * position is not in the formula.** Not difficult — **impossible.** The first
 * creature this game had was a þorskur computed as
 * `fishAt(x, z, surface, bed, seconds)`, and 🎯 he described the result
 * exactly: *"its a xerox copy of a picture of a cod floting in a sea that has
 * no movement."*
 *
 * 📖 **The standing specification is
 * [`Development/14_The_Animal_System.md`](../../../Development/14_The_Animal_System.md)**
 * and the reasoning is [ADR-0018]. **Read the spec before adding a creature.**
 *
 * ## 🔴 Why this is in `shared/` rather than beside the server's tick
 *
 * ⚠️ **`server/index.ts` cannot be imported without a database.** It pulls in
 * `db.ts`, which refuses to load without a connection string, **so a test file
 * beside it does not run on this workstation at all.** `presence.ts` carries the
 * same note for the same reason, and the rule both arrive at is:
 * **if a decision can be arithmetic over plain values, put it where a test can
 * reach it.**
 *
 * 🔴 **And there is a second reason here that presence does not have: the SPEAR
 * (T221).** The server must be able to agree that a creature was where a client
 * says it was. **Two copies of this logic would be two answers.**
 *
 * ## ⚠️ What this module deliberately is NOT
 *
 * No `three`, no geometry, no colour, **and no clock of its own.** It answers
 * *what does this creature want to do next, and where does it end up* — nothing
 * about how it is drawn (that is the client) and nothing about when the tick
 * happens (that is the socket).
 */

/** A place in the world, in metres. `y` is height. */
export interface Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/**
 * 🎲 **A creature's own stream of random numbers, and it is SEEDED.**
 *
 * 🔴 **D2: a random answer cannot be tested and cannot be reproduced.** Every
 * decision a creature makes comes out of here, so a trajectory can be replayed
 * exactly — which is what makes **T227's trajectory checks** possible at all.
 *
 * ⚠️ **NOT `Math.random()`, anywhere, ever.** A creature seeded from its own id
 * behaves the same way twice, and two creatures with different ids do not
 * behave alike. 🔴 **That second half is the fault haugur found by eye:**
 * *"they all act the same, all the same size, all move the same."*
 *
 * ⚠️ **And a weak mixer is how that fault happened.** `fish.ts` records it:
 * a salt xor-ed in at the end moved only a handful of bits, so *"how deep is
 * this fish"* and *"is there a fish here"* answered **0.087 to 0.125 across
 * fifty-five fish.** ***"It looked random"* was true of the broken one too.**
 * This is `mulberry32`, which avalanches properly.
 */
export interface Rng {
  /** The next number in `[0, 1)`. */
  (): number;
}

/** Make a creature's own generator from a seed. */
export function rngFrom(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b_79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
}

/** Turn a creature's id into a seed. The murmur3 finaliser, as `fish.ts` uses. */
export function seedFrom(id: string): number {
  let h = 0x2545_f491;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 0x85eb_ca6b);
  h ^= h >>> 15;
  h = Math.imul(h, 0x2545_f491);
  h ^= h >>> 13;
  h = Math.imul(h, 0x27d4_eb2d);
  return (h ^ (h >>> 16)) >>> 0;
}

/**
 * 🔴 **What a behaviour takes charge of.**
 *
 * ⚠️ **`move` and `look` are separate, and that separation is what makes an
 * animal look like it has a mind** — it can walk somewhere *and* watch you at
 * the same time. Minecraft calls these mutex flags and
 * [`Minecraft/18`](../../../Development/Minecraft/18_Animal_Movement_And_AI.md)
 * §1 has the full set; **two is enough for everything we are building.**
 */
export type Flag = 'move' | 'look';

/**
 * 🌍 **The world, as much of it as a creature can feel.**
 *
 * 🔴 **SYNCHRONOUS ON PURPOSE.** `ServerWorld.blockAt` is `async` and the
 * client's is not — a trap already written down in
 * [`06_Traps.md`](../../../Development/06_Traps.md). **The server pre-awaits
 * the chunks around a creature and hands in a snapshot**, so nothing in here
 * ever waits, and the same code runs unchanged in a test with a hand-built
 * world.
 */
export interface Ground {
  /**
   * Is the whole-metre cell containing this point solid?
   *
   * ⚠️ **Outside the loaded snapshot, answer `true`.** A creature must not walk
   * out of the world because nobody had loaded it yet — **an unknown cell is a
   * wall, not a doorway.**
   */
  solid(x: number, y: number, z: number): boolean;
  /** Is the cell water? Used by swimmers, and by *"can I stand here"* for walkers. */
  water(x: number, y: number, z: number): boolean;
  /**
   * 🌿 **WHAT the cell is made of — the block id, or `null` outside what is
   * loaded.**
   *
   * 🔴 **THIS IS WHAT MAKES GRAZING POSSIBLE, AND IT IS THE ONE PLACE THIS GAME
   * BEATS MINECRAFT WITHOUT TRYING.** Minecraft's cow appears to graze because
   * its wander scorer gives **grass underfoot 10 and everything else zero** —
   * one line, and `Minecraft/18` §2.2 calls it *"most of the difference between
   * an animal that random-walks and one that appears to be GRAZING."*
   *
   * ✅ **Ours can score the ACTUAL VEGETATION**, because the world is generated
   * from Iceland's own habitat raster and **the block under a sheep's feet IS
   * that habitat, already resolved** — `lyng`, `mói`, `mýri`, `tún`, `kjarr`.
   * 👉 **So a flock drifting uphill because the real ground there is better
   * grazing is the weather-and-terrain trick pointed at an animal.**
   *
   * ⚠️ **`solid` and `water` were not enough and could not be made enough.**
   * Both answer *may I be here*; **this answers *do I want to be here*, and no
   * amount of the first gives you the second.**
   */
  block(x: number, y: number, z: number): number | null;
}

import type { Home } from './lead.ts';
import { blockHeight, isSolid } from './blocks.ts';
import { type SchoolRules, slotFor, shoalStation } from './school.ts';

/** What a creature is, and all of it is remembered between ticks. */
export interface Creature {
  /** Stable for this creature's life. Never shown to a player. */
  readonly id: string;
  /** Which species — the key into the behaviour list and the body. */
  readonly kind: string;
  /** Where it is, in world metres. */
  at: Vec3;
  /** Which way it faces, radians, 0 = +x. */
  heading: number;
  /** Where it decided to go, or `null` for *"nowhere, and that is a decision"*. */
  goal: Vec3 | null;
  /** What it is looking at, or `null`. */
  lookAt: Vec3 | null;
  /**
   * 🔴 **THE NAME OF THE BEHAVIOUR IN CHARGE OF MOVING — and this is the whole
   * debugging story.**
   *
   * It goes **on the wire** and into an **F8 bug report**, the way the build
   * sha already does, so a report can say *"the sheep was `Fleeing` when it
   * walked through the wall."*
   *
   * ⚠️ **This is why the brain is a flat list and not a behaviour tree or a
   * weighted blend.** For a project whose owner cannot read the code (D2), **a
   * mind you cannot name is a mind you cannot fix.**
   */
  moving: string | null;
  /** The behaviour in charge of looking, by name. */
  looking: string | null;
  /**
   * 🐟 **The id of the creature this one is following, or `null` for *"I lead
   * myself"*** — T233.
   *
   * 🔴 **A FIRST-CLASS FIELD RATHER THAN A LINE IN {@link Creature.memory},
   * because schooling is not a fish detail.** 📖 `14_The_Animal_System.md` §④
   * settled it as the standing architecture for every group this game will ever
   * have: **one leader that thinks, followers that do not** — *"which is also
   * how thousands of animals become affordable"*. **A flock of sheep and a flock
   * of birds use this same field.**
   *
   * ⚠️ **`memory` could not hold it anyway** — it is `Record<string, number>`
   * and an id is a string — **and that limit is a good one, because it forced
   * the question of whether this belongs to a species or to the system.** It
   * belongs to the system.
   *
   * 🔴 **Never a chain.** A follower cannot be followed; `keepSchools` breaks
   * any link that would make one, because a chain can close into a ring that
   * follows itself for ever.
   */
  leader: string | null;
  /**
   * 🌾 **How far below `at.y` this creature's feet are DRAWN** — set every tick
   * by the herd from {@link sinkUnder}, absent for nothing.
   *
   * 🔴 **Walking stays in whole metres on purpose** — a sheep hops 1 m and
   * steps down 3 (D130) — so a ground lower than its cell changes nothing about
   * where a sheep can go. **It only changes where its feet touch.** This is that
   * difference, and the wire sends `at.y - sink`.
   */
  sink?: number;
  /**
   * 🙌 **Does somebody OWN this one** — T261, D123.
   *
   * 🔴 **THE WHOLE OF PERSISTENCE IS THIS BOOLEAN**, and it is
   * `Minecraft/06_Mobs.md` §5's best idea: instead of a curated list of
   * protected species, **the game infers value from PLAYER ACTION.** *"You do
   * not declare a cow important; you show it, by feeding it."*
   *
   * | | |
   * |---|---|
   * | `false` — **ambient** | spawned near whoever is standing about, **forgotten when they leave, never written down.** Correct for an animal nobody owns |
   * | `true` — **kept** | **a row in the `creatures` table.** Never forgotten at distance, and **alive after a restart** |
   *
   * ✅ **Today exactly one thing sets it: carrying a sheep** (`carry.ts`).
   * 🚳 **The kaupskip (D123) will be the second**, and breeding the third.
   *
   * ⚠️ **It changes nothing about how the creature THINKS.** A kept sheep
   * wanders exactly like an ambient one — D123 says so in as many words:
   * *"they wander unless they are in a pen."* 🔴 **This field is about whether
   * the world remembers it, never about what it does.**
   */
  kept: boolean;
  /**
   * 🔒 **WHO owns it — the account id, or absent** — D125.
   *
   * 🎯 **haugur:** you may kill a wild sheep or your own, **never somebody
   * else's.** ⚠️ **Absent means nobody the server can name** — an ambient
   * animal, or one kept by an anonymous session on development — **and anybody
   * may strike those.** It mirrors `creatures.owner_user_id`, which the database
   * already had; until D125 nothing read it back.
   */
  owner?: string;
  /**
   * 🐣 **The ewe that bore it**, by id — D130. Absent for a sheep that was not
   * born in the game. ⚠️ Not in `memory`, which holds numbers; the same reason
   * `owner` is here.
   */
  mother?: string;
  /** Its own stream of random numbers — seeded from {@link Creature.id}. */
  readonly rng: Rng;
  /** Whatever the species' behaviours need to remember. Opaque here on purpose. */
  readonly memory: Record<string, number>;
}

/** Everything a behaviour is allowed to know when it decides. */
export interface Sense {
  readonly self: Creature;
  /** Seconds since the world began — the same clock everything else reads. */
  readonly now: number;
  /** The ground, already loaded. */
  readonly ground: Ground;
  /**
   * 🔴 **The nearest player, and this field is the entire reason D117 exists.**
   *
   * 🎯 **haugur:** *"yes animals should notice you, thats important."*
   * ⚠️ **`null` when nobody is near enough to matter** — which is most of the
   * time, and a behaviour that reads it must handle that first.
   */
  readonly nearestPlayer: {
    readonly at: Vec3;
    readonly distance: number;
    /**
     * 🔴 **How fast they are moving, metres per second — and this field is what
     * makes *"the skill is patience"* buildable at all.**
     *
     * 🎯 **haugur, 2026-09-17:** *"fish should be sceptical of you but we still
     * need to be able to hunt them."*
     *
     * ⚠️ **Without it a wary creature can only ever answer *how close*, and a
     * fish that flees whenever you are near is a fish nobody can ever spear.**
     * With it, **holding still is a thing a player can DO** — which is what real
     * spearfishing is, and it costs one number.
     */
    readonly speed: number;
  } | null;
  /**
   * 🐟 **Where this creature's leader is, or `null` when it leads itself** —
   * T233.
   *
   * 🔴 **Resolved by whoever owns the herd, not looked up by the behaviour.** A
   * behaviour can see one creature — itself — and that is the whole point of
   * {@link Sense}: **the brain is handed what it may know.** Letting `Follow`
   * reach into a list of every animal in the world would make it the one
   * behaviour that needs the server to test.
   *
   * ⚠️ **`heading` is here because a slot has to TURN with the leader.** A
   * formation that did not rotate would make a shoal swim sideways the moment
   * its leader turned.
   */
  readonly leader: {
    readonly at: Vec3;
    readonly heading: number;
  } | null;
  /**
   * ⭕ **Where it must stay, or absent** — D128. A led sheep's home is its holder
   * (3 m); a tied sheep's is its peg (5 m). The server resolves it each beat, the
   * way `leader` is resolved. **Optional**, so a fixture that knows nothing of
   * leads builds exactly as before.
   */
  readonly home?: Home | null;
  /**
   * 🎣 **A float to swim to, or absent** — D129. Where the bait hangs, world
   * metres at the water's surface. The server resolves it, the way it resolves
   * a lead: a float names a person, and memory holds numbers.
   */
  readonly lure?: Vec3 | null;
  /**
   * 💞 **The sheep this one is walking to, or absent** — D130. Two ready sheep in
   * one pen; the server pairs them and hands each the other's place.
   */
  readonly mate?: Vec3 | null;
  /** 🐣 **A lamb's mother, or absent** — D130. Where she is, each beat. */
  readonly mother?: Vec3 | null;
  /**
   * 🪨 **The cells of the pen it is in, or absent** — D130, as `x,y,z` keys from
   * `penAround`. When there is one, wander spots are chosen INSIDE it: a
   * penned sheep grazed the pen instead of standing against the wall.
   */
  readonly pen?: ReadonlySet<string> | null;
  /**
   * 🕰️ **The hour of the GAME day, 0–24, or absent** — T288. A fox sleeps
   * 09–17. ⚠️ The world clock runs 72× faster than `now`, so a behaviour that
   * wants the time of day must be handed it rather than work it out.
   */
  readonly hour?: number;
  /**
   * 🦊🐑 **A lamb this creature is hunting, or absent** — T288, decision 134.
   * The server picks it (a lamb outside a pen, near enough) and takes it when
   * the hunter reaches it; the brain only walks there.
   */
  readonly prey?: Vec3 | null;
}

/** What a behaviour wants to happen. Every field is optional and means *no opinion*. */
export interface Intent {
  /**
   * Where to go. `null` means *forget where I was going* — and the herd then
   * picks somewhere new, which is how {@link wander} re-rolls.
   */
  readonly goal?: Vec3 | null;
  /**
   * 🧍 **Stand still this tick** — do not walk, and do not pick a destination.
   *
   * 🎯 **haugur, 2026-10-01:** *"sheep dont stand still while grasing, sheep
   * stop to eat in nature."* 📏 **Measured on dev: every grazing sheep walked
   * at its full 0.9 m/s** while the wire said `Graze`. 🔴 **The cause was
   * `goal: null` meaning two opposite things**: this field's doc said *stand
   * still*, and `tickCreature` read it as *pick somewhere new* — so grazing and
   * a sheep that had reached its mate both walked off at once. ✅ **Standing
   * still is now its own word.**
   */
  readonly hold?: true;
  /** What to look at. */
  readonly lookAt?: Vec3 | null;
  /**
   * How fast, as a multiple of the species' cruising speed.
   *
   * 🔴 **Minecraft gives every goal its own speed multiplier, and that alone
   * reads as emotion** — a cow strolls at 0.8× and flees at 2×.
   */
  readonly pace?: number;
  /**
   * 🔄 **How fast it may turn, as a multiple of the species' own turn rate.**
   *
   * 🔴 **Added for D125, and a test found why.** A struck sheep facing you
   * turned at its calm walking rate — so for over a second it RAN AT YOU while
   * slowly swinging round. ⚠️ **A startled animal spins on the spot**; a
   * grazing one turns its whole body slowly. Two moods, two turn rates.
   */
  readonly turn?: number;
}

/** One small, named thing a creature might want to do. */
export interface Behaviour {
  /** 🔴 **Shown in bug reports. Make it a word a person can act on.** */
  readonly name: string;
  /** **Lower runs first.** Ties are broken by the order in the list. */
  readonly priority: number;
  /** What it takes charge of while it runs. */
  readonly flags: readonly Flag[];
  /** Does it want to run right now? Cheap — this is asked of every behaviour, every tick. */
  wants(sense: Sense): boolean;
  /** What it wants. Only called when it has won its flags. */
  act(sense: Sense): Intent;
}

/** Which behaviour holds each flag this tick. */
export interface Chosen {
  readonly move: Behaviour | null;
  readonly look: Behaviour | null;
}

/**
 * 🧠 **Pick who is in charge — the whole arbitration, and it is eight lines.**
 *
 * **Walk the list in priority order. The first behaviour that wants to run AND
 * whose flags are all still free, claims them.** That is it. No tree, no
 * scoring, no blending.
 *
 * ⚠️ **A behaviour with NO flags can always run**, and that is deliberate: it
 * is how a thing that only remembers something joins the list without fighting
 * anybody for control.
 *
 * 🔴 **The property this buys, and it is the point:** at any instant exactly one
 * behaviour owns `move` and exactly one owns `look`, **and both have names.**
 */
export function choose(behaviours: readonly Behaviour[], sense: Sense): Chosen {
  const order = [...behaviours].sort((a, b) => a.priority - b.priority);
  let move: Behaviour | null = null;
  let look: Behaviour | null = null;
  for (const b of order) {
    const wantsMove = b.flags.includes('move');
    const wantsLook = b.flags.includes('look');
    if (wantsMove && move !== null) continue;
    if (wantsLook && look !== null) continue;
    if (!b.wants(sense)) continue;
    if (wantsMove) move = b;
    if (wantsLook) look = b;
  }
  return { move, look };
}

/**
 * 🚶 **Can a creature of this shape be at this point?**
 *
 * A species supplies one of these. A walker wants solid ground under it and air
 * at head height; a swimmer wants water and no rock.
 */
export interface Fits {
  (ground: Ground, at: Vec3): boolean;
}

/**
 * 🧱 **A CREATURE IS NOT A POINT — GIVE IT A FOOTPRINT.**
 *
 * 🎯 **haugur, 2026-09-24:** *"look at the sheep half way in the wall"* — and
 * *"sheep walk through all sorts of things."* 📷 **[#772] shows four of them
 * with their back halves inside an earth terrace.**
 *
 * 🔴 **EVERY `fits` CHECK IN THIS SYSTEM TESTED ONE POINT: the animal’s
 * CENTRE.** So a body **1.07 m long** could have half of itself inside a wall
 * and the server saw nothing wrong — the centre was in clear air, which is
 * all anybody ever asked.
 *
 * ✅ **This wraps a species’ own rule and asks it at the four corners of the
 * body as well.** 🔴 **The species rule is unchanged** — *not in the sea, not
 * in a tree, not on a roof, something solid underfoot* — it is simply asked
 * in more than one place.
 *
 * 📐 **A SQUARE footprint, and that is deliberate.** Mojang uses one for every
 * mob, and the reason is that **a rectangle has to rotate with the animal** —
 * which makes *can I be here* depend on which way I am facing, and a creature
 * that fits walking north and not walking east reads as a bug from every
 * angle. ⚠️ **So it is the animal’s WIDTH, never its length**: a sheep is
 * 1.07 m long and 0.42 m across, and a 1.07 m square would refuse doorways it
 * walks through sideways.
 *
 * ⚠️ **It costs four more block reads per candidate**, and `stepBody` tries up
 * to four candidates a tick. 📏 **The server is not the scarce thing** — the
 * player’s main thread is (T218) — and this runs nowhere near it.
 */
export function withFootprint(fits: Fits, width: number): Fits {
  const half = width / 2;
  return (ground, at) => {
    if (!fits(ground, at)) return false;
    for (const dx of [-half, half]) {
      for (const dz of [-half, half]) {
        if (!fits(ground, { x: at.x + dx, y: at.y, z: at.z + dz })) return false;
      }
    }
    return true;
  };
}

/**
 * 🧱🪜 **A BODY IS CLEAR EVERYWHERE, BUT IT ONLY NEEDS TO STAND SOMEWHERE.**
 *
 * 🔴 **{@link withFootprint} asked for ground under all four corners, and that
 * made every 1 m step impassable in BOTH directions** — measured 2026-09-29
 * with the game's own sheep: halfway up a step the back corners hang over the
 * lower terrace, so no position on the way up or down ever fitted. **A sheep
 * lived on whatever flat patch it stood on.**
 *
 * ✅ **So the one question is split in two, the way Minecraft's box works:**
 *
 * | | Asked at | Why |
 * |---|---|---|
 * | **`clear`** — no part of the body inside a wall, the sea or a tree | the centre AND all four corners | 🎯 *"look at the sheep half way in the wall"* — this half of the old rule is unchanged |
 * | **`support`** — something it will stand on under its feet | **any one** of the five | a sheep may overhang an edge, which is what getting up and down one looks like |
 *
 * 📐 **Still SQUARE and still the animal's width**, for the reason
 * {@link withFootprint} gives.
 */
export function withBody(clear: Fits, support: Fits, width: number): Fits {
  const half = width / 2;
  return (ground, at) => {
    const points = [
      at,
      { x: at.x - half, y: at.y, z: at.z - half },
      { x: at.x - half, y: at.y, z: at.z + half },
      { x: at.x + half, y: at.y, z: at.z - half },
      { x: at.x + half, y: at.y, z: at.z + half },
    ];
    if (!points.every((p) => clear(ground, p))) return false;
    return points.some((p) => support(ground, p));
  };
}
/**
 * 🌿 **How much a creature would like to be here.** Higher is better.
 *
 * 🔴 **THIS IS WHERE THE LIFE COMES FROM, AND IT IS ONE LINE PER SPECIES.**
 * Minecraft's default scorer returns **zero everywhere**, so best-of-ten
 * collapses to *first acceptable* — a random walk. Its animals override it with
 * a single line: **grass underfoot scores 10.** ✅ **That one line is most of
 * the difference between an animal that random-walks and one that appears to be
 * GRAZING.**
 *
 * ✅ **And it is where this game beats Minecraft without trying**, because we
 * have the real habitat raster for the whole island: ours can score **the actual
 * vegetation under the animal's feet**.
 */
export interface Score {
  (ground: Ground, at: Vec3): number;
}

/** How a species wanders. Every number here is a dial. */
export interface WanderShape {
  /** How far it will consider going, horizontally, in metres. */
  readonly radius: number;
  /** How many places it thinks of before choosing. Minecraft uses ten. */
  readonly candidates: number;
}

/**
 * 📏 **Where would this creature sit in this column of the world — or nowhere?**
 *
 * 🔴 **THIS IS WHERE "MEASURE THE ROOM FIRST" LIVES, AND IT IS THE WHOLE REASON
 * THE WANDER DOES NOT CHOOSE A HEIGHT ITSELF.**
 *
 * ⚠️ **Found by a test on the first run: a walker that picks its own height is
 * wrong nineteen times out of twenty.** Given ten metres of reach and seven of
 * rise, a sheep's candidates land in mid-air or inside the hill, and **only 19
 * of 60 sheep found anywhere to stand at all.** The ground decides a walker's
 * height; the walker does not.
 *
 * 🔴 **And for a swimmer it is the fish's worst fault, expressed once and
 * correctly:** measure the water column that is actually there, **then** choose
 * inside it. Never choose a depth and squash it against the bed.
 *
 * ⚠️ **`null` means *this column is no good* — discard the candidate.** It never
 * means *put it at the nearest legal place*, which is the clamp.
 */
export interface Place {
  (ground: Ground, x: number, z: number, near: Vec3, rng: Rng): Vec3 | null;
}

/**
 * 🚶 **A walker stands on whatever the ground turns out to be.**
 *
 * Scans the column near where the creature already is and takes the first
 * standable surface: solid underfoot, clear at foot and head height.
 *
 * @param rise how far up or down it will look for ground, in metres
 * @param headroom how much clear air it needs above its feet
 */
/**
 * 🌾 **How far below a standing creature's `y` the ground really is** — D131,
 * #813.
 *
 * 🎯 **haugur asked for stubble a quarter metre lower than the field**, so a cut
 * reads from across it. A walker's `y` is the top of the CELL under it, and
 * that is 25 cm above the top of a stubble — **a sheep there would float.**
 * ⚠️ **This measures the thing under the thing**: the block's own height, not
 * its cell.
 *
 * Zero for a full block, for anything not solid, and for ground not loaded.
 */
export function sinkUnder(ground: Ground, at: Vec3): number {
  const under = ground.block(at.x, at.y - 0.5, at.z);
  if (under === null || !isSolid(under)) return 0;
  const height = blockHeight(under);
  return height < 1 ? 1 - height : 0;
}

export function placeOnGround(rise: number, headroom: number): Place {
  return (ground, x, z, near) => {
    for (let y = Math.round(near.y + rise); y >= Math.round(near.y - rise); y--) {
      if (!ground.solid(x, y - 0.5, z)) continue;
      if (ground.solid(x, y, z)) continue;
      if (ground.solid(x, y + headroom, z)) continue;
      return { x, y, z };
    }
    return null;
  };
}

/**
 * 🐟 **A swimmer measures the water that is there, then picks inside it.**
 *
 * 🎯 **haugur, #653:** *"All the fish are in a line, all fish live exactly on
 * the same plane, that is not"* — and his photograph showed the shoal lying
 * just off the gravel.
 *
 * 🔴 **The cause was a clamp.** The old code chose a depth of 0.6–3.5 m and
 * *then* squashed it against the sea floor. In the shallows — which is most of
 * the shore — **nearly every choice landed on the same number.**
 *
 * ✅ **Here the floor and the ceiling are found FIRST**, and if they cross, the
 * column is refused rather than forced. **In 8 m of sea a fish can be anywhere
 * in metres of it; in 1.5 m of sea it still has somewhere to be; in 40 cm of
 * water there is no fish.**
 *
 * @param aboveBed how far off the floor the lowest fish swims
 * @param belowSurface how far under the top the highest fish swims
 * @param reach how far up and down to look for the bed and the surface
 */
export function placeInWater(aboveBed: number, belowSurface: number, reach: number): Place {
  return (ground, x, z, near, rng) => {
    const top = Math.round(near.y + reach);
    const bottom = Math.round(near.y - reach);
    /* Find the surface: the highest water in this column. */
    let surface: number | null = null;
    for (let y = top; y >= bottom; y--) {
      if (ground.water(x, y, z)) {
        surface = y + 1;
        break;
      }
    }
    if (surface === null) return null;
    /* Find the bed: the highest solid below that surface. */
    let bed: number | null = null;
    for (let y = surface; y >= bottom; y--) {
      if (ground.solid(x, y, z)) {
        bed = y + 1;
        break;
      }
    }
    if (bed === null) bed = bottom;

    /* 🔴 The room, measured. Then the choice, inside it. Never the other way. */
    const floor = bed + aboveBed;
    const ceiling = surface - belowSurface;
    if (ceiling < floor) return null;
    return { x, y: floor + rng() * (ceiling - floor), z };
  };
}

/**
 * 🎯 **Pick somewhere to go — and this function is where the shoal stood in a
 * row.**
 *
 * > **Pick ONE point. Walk to it. Forget it.** Not a path, not a plan, not a
 * > patrol route.
 *
 * 🔴 **MEASURE THE ROOM FIRST AND CHOOSE INSIDE IT. NEVER CHOOSE FREELY AND
 * THEN CLAMP.**
 *
 * ⚠️ **This is the single most expensive lesson the fish taught.** `fish.ts`
 * picked a depth of 0.6–3.5 m and *then* clamped it against the sea floor. In
 * the shallows — which is most of the shore — nearly every choice fell below the
 * floor, **so they all clamped to the same number**, and 🎯 haugur photographed
 * the result: *"All the fish are in a line, all fish live exactly on the same
 * plane."*
 *
 * ✅ **So a candidate that does not fit is DISCARDED, never squashed onto the
 * limit.** If none of them fit, the answer is **`null` — stand still** — which
 * is an honest failure and looks like an animal pausing.
 *
 * ⚠️ **`rise` is smaller than `radius` on purpose.** A creature that considers
 * going as far up as along reads as a balloon; Minecraft's ±10 horizontal and
 * ±7 vertical is the ratio, not the numbers.
 */
export function chooseDestination(
  self: Creature,
  ground: Ground,
  shape: WanderShape,
  place: Place,
  score: Score,
  /**
   * ⭕ **A home circle to choose INSIDE** — D128. When there is one, the disc is
   * the circle itself, centred on the home: *measure the room first and choose
   * inside it*, never choose freely and clamp.
   */
  home: Home | null = null,
  /** 🪨 A pen's cells, as `x,y,z` — D130. A spot not in it is not considered. */
  pen: ReadonlySet<string> | null = null,
): Vec3 | null {
  const centre = home === null ? self.at : home.at;
  const radius = home === null ? shape.radius : Math.min(shape.radius, home.radius);
  let best: Vec3 | null = null;
  let bestScore = -Infinity;
  for (let i = 0; i < shape.candidates; i++) {
    const angle = self.rng() * Math.PI * 2;
    /* √ of a uniform spreads the points evenly over the disc rather than
       bunching them at the centre, which is what a bare uniform radius does. */
    const reach = Math.sqrt(self.rng()) * radius;
    const x = centre.x + Math.cos(angle) * reach;
    const z = centre.z + Math.sin(angle) * reach;
    /* 🔴 The COLUMN decides the height, and refuses outright if there is no
       room. Rejected, never clamped — this is the whole point. */
    const at = place(ground, x, z, self.at, self.rng);
    if (at === null) continue;
    /* 🪨 Inside the pen or not at all — rejected, never clamped. */
    if (pen !== null && !pen.has(`${Math.floor(at.x)},${Math.round(at.y)},${Math.floor(at.z)}`))
      continue;
    const s = score(ground, at);
    if (s > bestScore) {
      bestScore = s;
      best = at;
    }
  }
  return best;
}

/** How a creature's body behaves in the world. All per species. */
/**
 * 🪨 **How fast a creature falls, metres a second.**
 *
 * ⚠️ **A DIAL and not haugur’s number.** Deliberately gentle — a creature
 * that snaps to the ground reads as a teleport, and the thing being fixed was
 * an animal in the wrong place rather than an animal falling too slowly.
 * 🔴 **Real gravity accelerates; this does not.** Adding a velocity to every
 * creature is a bigger change than this fault deserves, and it is said here
 * rather than discovered later.
 */
export const FALL_SPEED = 4;

/**
 * 🪨 **How far below itself a stranded creature will look for somewhere to land.**
 *
 * 🔴 **A fall needs a DESTINATION, not just a direction.** ⚠️ `stepBody` is
 * shared with the fish, and falling with no target would sink one through the
 * seabed — **a buried animal is worse than a stuck one, and much harder to
 * see.** ✅ With no landing inside this reach a creature simply stays put, which
 * is what it did before gravity existed.
 */
export const FALL_REACH = 24;

export interface Physique {
  /** Cruising speed in metres per second, before a behaviour's `pace`. */
  readonly speed: number;
  /** How fast it can turn, radians per second. ⚠️ **A creature that turns instantly reads as a machine.** */
  readonly turn: number;
  /**
   * How tall a lip it can get up, in metres. `0` for a swimmer.
   *
   * 🔴 **A WALKER'S IS 1 — A HOP, NOT MINECRAFT'S 0.6 `step_height`.** Every
   * slope in this world is a staircase of whole-metre blocks, and at 0.6 a
   * sheep could climb none of them: it lived on whatever flat patch it stood
   * on. Minecraft's mobs get up a block by JUMPING (`jump_strength` 0.42 ≈ a
   * 1.25-block hop, `Minecraft/18` §5.3); this is that hop as one number.
   */
  readonly step: number;
  /**
   * 🪜 **How far it will step DOWN off a ledge**, whole metres. Absent or `0`
   * for a swimmer, which chooses its own height.
   *
   * 🔴 **The way down never existed** — gravity catches a creature that is
   * already somewhere it cannot be, but nothing let a walker walk OFF an edge.
   * ⚠️ 3 is Minecraft's pathfinding fall limit for ordinary mobs, from memory
   * rather than from `Minecraft/18` — a dial.
   */
  readonly drop?: number;
}

/**
 * 🧱 **Move, THEN check what you hit — and this runs every tick whether or not
 * the brain did anything.**
 *
 * 🔴 **IT IS THE ANSWER TO *"THEY DISAPEAR INTO THE SHORELINE."*** Our fish had
 * no step like this at all — a formula recomputes its position from scratch and
 * there is nothing to push against — **which is why every fix ever made to them
 * was a clamp. A clamp is the only tool a formula has.**
 *
 * **Axis by axis**, because a creature sliding along a wall is what a diagonal
 * blocked all at once cannot do. ⚠️ **Undo the axis that failed, keep the
 * others** — that is the sliding.
 *
 * ⚠️ **It returns a NEW position and changes nothing.** The caller decides
 * whether to accept it, which is what lets a test run a hundred ticks and keep
 * every one of them — {@link Creature} is mutable but this is not what mutates
 * it.
 */
export function stepBody(
  /* ⚠️ reassigned by the fall above — a creature may not start the tick
     where the caller thought it was. */
  from: Vec3,
  toward: Vec3 | null,
  heading: number,
  physique: Physique,
  pace: number,
  ground: Ground,
  fits: Fits,
  dt: number,
): { at: Vec3; heading: number; blocked: boolean } {
  /**
   * 🪨 **GRAVITY — AND IT RUNS BEFORE ANYTHING ELSE, INCLUDING *ARRIVED*.**
   *
   * 🔴 **THE FAULT IT EXISTS TO FIX: a creature that gets somewhere it cannot
   * be stays there for ever.** ⚠️ **`stepBody` refuses every move that does
   * not `fit`, and a creature standing somewhere illegal fails them all** — so
   * it is not merely floating, it is **stranded and frozen.**
   *
   * 🎯 **haugur found it:** *"they walk on the ground but on top of kjarr…
   * they look like they are floating a little."* 📏 **Measured on dev before
   * the fix: 7 of 10 sheep stood 0.4–2.8 m above the terrain, and 8 of 10 had
   * not moved a centimetre in forty seconds.**
   *
   * 🔴 **The way UP already existed and the way DOWN never did.** The step-up
   * below lifts a creature over a lip whenever a flat move is refused — so
   * over minutes it RATCHETS up anything climbable, and with nothing pulling
   * it back it ends up in the canopy. 👉 ***A step up with no fall is a
   * ladder.***
   *
   * ⚠️ **It is a SEARCH downward rather than an acceleration.** This game has
   * no creature velocity and adding one is a bigger change than this fault
   * deserves; a limited drop per tick reads as a fall and cannot tunnel
   * through a floor, because every candidate must `fit`.
   */
  if (!fits(ground, from)) {
    /* 🔴 **ONLY FALL TOWARD SOMEWHERE IT COULD ACTUALLY BE.**
       ⚠️ **`stepBody` is shared with the FISH**, and a free fall with no target
       would sink a fish that momentarily does not fit straight through the
       seabed — trading a stuck animal for a buried one, which is worse and
       harder to see. ✅ So look for a landing first; if there is none below,
       leave it where it is, which is exactly the old behaviour. */
    let landing: number | null = null;
    for (let d = 0.5; d <= FALL_REACH; d += 0.5) {
      if (fits(ground, { x: from.x, y: from.y - d, z: from.z })) {
        landing = from.y - d;
        break;
      }
    }
    if (landing !== null) {
      const drop = Math.max(FALL_SPEED * dt, 0.05);
      from = { x: from.x, y: Math.max(landing, from.y - drop), z: from.z };
    }
  }

  if (toward === null) return { at: from, heading, blocked: false };

  const dx = toward.x - from.x;
  const dz = toward.z - from.z;
  const flat = Math.hypot(dx, dz);
  /* Arrived. ⚠️ Half a metre, not zero — a creature that must hit a point
     exactly never arrives, and jitters on the spot forever. */
  if (flat < 0.5) return { at: from, heading, blocked: false };

  /* 🔴 Turn toward it at a limited rate rather than snapping. The shortest way
     round, so a creature never spins the long way to face something behind it. */
  const want = Math.atan2(dz, dx);
  let delta = ((want - heading + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
  const most = physique.turn * dt;
  if (delta > most) delta = most;
  if (delta < -most) delta = -most;
  const facing = heading + delta;

  /* How far it gets this tick, never past the goal. */
  const travel = Math.min(physique.speed * pace * dt, flat);
  const wantX = from.x + Math.cos(facing) * travel;
  const wantZ = from.z + Math.sin(facing) * travel;
  /* Height moves toward the goal at the same rate, so a fish rises smoothly
     rather than teleporting up when it arrives. */
  const rise = toward.y - from.y;
  const wantY = from.y + Math.sign(rise) * Math.min(Math.abs(rise), physique.speed * pace * dt);

  let at: Vec3 = from;
  let blocked = false;

  /* ── x, then z, then y. Each one undone alone if it does not fit. ── */
  const tryX = { x: wantX, y: at.y, z: at.z };
  if (fits(ground, tryX)) at = tryX;
  else blocked = true;

  const tryZ = { x: at.x, y: at.y, z: wantZ };
  if (fits(ground, tryZ)) at = tryZ;
  else blocked = true;

  /* 🐟 ONLY A SWIMMER CHOOSES ITS OWN HEIGHT. 🔴 A walker's comes from the
     ground — the step up, the step down and gravity. ⚠️ Until 2026-09-29 a
     walker drifted toward its goal's height here too, and a sheep aiming at a
     higher terrace rose half a metre into the air in front of it and stood
     there: `standsOn` looks for footing half a metre down, so nothing refused
     it. */
  if (physique.step === 0) {
    const tryY = { x: at.x, y: wantY, z: at.z };
    if (fits(ground, tryY)) at = tryY;
    else blocked = true;
  }

  /* 🚶 A lip it can get up. ⚠️ Only tried when the flat move was refused —
     a walker that steps up every tick climbs walls. */
  if (blocked && physique.step > 0) {
    const up = { x: wantX, y: at.y + physique.step, z: wantZ };
    if (fits(ground, up)) {
      at = up;
      blocked = false;
    }
  }

  /* 🪜 And a ledge it can get down — the nearest landing first, so a sheep
     steps down one metre rather than jumping three when one would do. */
  const drop = physique.drop ?? 0;
  for (let d = 1; blocked && d <= drop; d++) {
    const down = { x: wantX, y: at.y - d, z: wantZ };
    if (fits(ground, down)) {
      at = down;
      blocked = false;
    }
  }

  return { at, heading: facing, blocked };
}

/**
 * 🐑 **Everything that makes one kind of creature what it is.**
 *
 * 🔴 **You do not write an animal. You write one of these.** A sheep and a fish
 * differ only in which behaviours are in the list and what the numbers are —
 * which is the whole return on D117, collected on the first day.
 */
export interface Species {
  /** The key the body and the wire use. */
  readonly kind: string;
  /** 🔴 **In any order. {@link choose} sorts by priority** — the list is a set, not a sequence. */
  readonly behaviours: readonly Behaviour[];
  readonly physique: Physique;
  readonly wander: WanderShape;
  /** Where this creature can be in a column — the ground decides, not the creature. */
  readonly place: Place;
  /** 🌿 How much it would like to be somewhere. **One line, and it is where grazing comes from.** */
  readonly score: Score;
  /**
   * 🐟 **How this species schools, or `null` for one that does not** — T233.
   *
   * 🔴 **`null` is a real answer and not an omission.** A solitary animal is a
   * species with no school rules, the same way a swimmer is a species whose
   * `step` is zero — **a number, not a code path.**
   */
  readonly school?: SchoolRules | null;
  /** Whether it can be at a point at all — used every tick by the collide step. */
  readonly fits: Fits;
  /**
   * 🐑 **How big it is for BUMPING INTO OTHER BODIES, or `null` for one that
   * nothing bumps into** — T274, `crowd.ts`.
   *
   * 🔴 **`null` is a real answer.** A fish is a species with no body here, and a
   * shoal passes through itself exactly as it always has — the same way a
   * solitary animal is one with no `school`. **A number, not a code path.**
   */
  readonly body?: { readonly width: number; readonly height: number } | null;
}

/**
 * 🚶 **Wander — *pick one point, walk to it, forget it*.**
 *
 * 🔴 **The single most important behaviour in the system**, and it is tiny.
 * Minecraft's cow re-rolls on about a 1-in-120 chance per tick — **roughly once
 * every six seconds** — and that irregularity is most of why the result reads as
 * unscripted rather than as a patrol.
 *
 * ⚠️ **It re-rolls on a TIMER THAT VARIES, not on arrival.** A creature that
 * only re-rolls when it arrives walks in clean segments between waypoints, which
 * is exactly what a machine looks like; one that changes its mind part-way
 * through is what an animal looks like.
 *
 * @param priority where it sits in the list — **last, by convention**: wander is
 *   what a creature does when nothing more pressing is true.
 * @param everySeconds average seconds between re-rolls.
 */
export function wander(priority: number, everySeconds: number): Behaviour {
  return {
    name: 'Wander',
    priority,
    flags: ['move'],
    /* Always available. It is the floor of the list. */
    wants: () => true,
    act(sense) {
      const self = sense.self;
      const due = self.memory.wanderAt ?? 0;
      if (self.goal !== null && sense.now < due) return { goal: self.goal, pace: 1 };
      /* ⚠️ Half to one and a half times the interval, so a herd does not think
         in unison — the same reason Minecraft staggers AI by entity id. */
      self.memory.wanderAt = sense.now + everySeconds * (0.5 + self.rng());
      return { goal: null, pace: 1 };
    },
  };
}

/**
 * 🐟 **Follow — keep your place in the shoal, and do not think about where to
 * go.**
 *
 * 🔴 **THIS IS THE WHOLE OF SCHOOLING FROM THE BRAIN'S SIDE, AND IT IS TWENTY
 * LINES.** Everything else about it — who leads whom, when a school forms, when
 * it breaks — is in [`school.ts`](school.ts), because that is a fact about the
 * group and this is a decision by one animal.
 *
 * 📖 **Java's rule, read off its own source in `Minecraft/18` §3.3a:**
 * `canRandomSwim()` returns `!isFollower()` — **a follower is removed from the
 * wandering goal entirely.** ✅ **We get that for free from the priority list:**
 * this sits ABOVE `wander`, so whenever a creature has a leader, `Follow` wins
 * and `Wander` never runs.
 *
 * 🔴 **AND IT SITS BELOW `Wary`, WHICH IS THE PART THAT MAKES IT LOOK ALIVE.**
 * When somebody wades in, every fish in the shoal — leader and followers alike —
 * finds `Wary` more pressing and **the school scatters.** When they hold still
 * it stops being pressing, `Follow` takes over again, and **the shoal re-forms
 * on its own.** ⚠️ **Nothing was written to make that happen. It falls out of
 * two priorities**, which is the argument for a flat list rather than a tree.
 *
 * ⚠️ **A follower keeps a SLOT rather than swimming at the leader** — see
 * `school.ts`, where the deviation from Java and its reason are written down.
 *
 * @param priority **above wander and below anything urgent.**
 */
export function follow(priority: number, rules: SchoolRules): Behaviour {
  return {
    name: 'Follow',
    priority,
    flags: ['move'],
    /* 🪢 D128 — a led or tied animal keeps to its own circle, not the flock's. */
    wants: (sense) =>
      sense.self.leader !== null && sense.leader !== null && (sense.home ?? null) === null,
    act(sense) {
      const leader = sense.leader;
      /* Cannot happen — `wants` returned true — but a `!` here would be the
         assertion that is eventually wrong. */
      if (leader === null) return {};
      return {
        goal: shoalStation(leader.at, leader.heading, slotFor(sense.self.id, rules)),
        /* 🔴 A shade faster than cruising, or a follower can never close a gap
           its leader opened and the shoal stretches into a line. ⚠️ Not fast
           enough to overshoot: `stepBody` never travels past the goal. */
        pace: 1.15,
      };
    },
  };
}

/**
 * ⏱️ **One creature, one tick — decide, then move, then remember what it was
 * doing.**
 *
 * 🔴 **It MUTATES the creature**, which is the one place in this module that
 * does. Everything above is arithmetic over values precisely so that this is the
 * only thing a test has to reason about as a state change.
 *
 * ⚠️ **The order matters and is not arbitrary:** choose who is in charge → ask
 * them what they want → move the body → **write down the name.** A body moved
 * before the brain ran would be a tick behind for ever, and the name written
 * before the move would describe the wrong tick in a bug report.
 *
 * 🔴 **`stepBody` runs whether or not a behaviour asked for anything.** The
 * collide step is not the brain's to skip — *that* is the answer to *"they
 * disapear into the shoreline."*
 */
export function tickCreature(self: Creature, species: Species, sense: Sense, dt: number): void {
  /* 💀 **A killed animal thinks nothing and goes nowhere** — D125. It lies where
     it fell until the herd lets it go, and `Dead` is the name on the wire that
     tells every client to tip it over. ⚠️ Checked by the field rather than by
     importing `slaughter.ts`, which already imports this file. */
  if (self.memory.deadAt !== undefined) {
    self.goal = null;
    self.lookAt = null;
    self.moving = 'Dead';
    self.looking = null;
    return;
  }
  const { move, look } = choose(species.behaviours, sense);

  let pace = 1;
  let turn = 1;
  let hold = false;
  if (move !== null) {
    const intent = move.act(sense);
    if (intent.pace !== undefined) pace = intent.pace;
    if (intent.turn !== undefined) turn = intent.turn;
    if (intent.goal !== undefined) self.goal = intent.goal;
    /* 🧍 Standing still forgets the goal too, so it does not walk on afterwards
       toward somewhere chosen before it stopped. */
    if (intent.hold === true) {
      hold = true;
      self.goal = null;
    }
  }
  if (look !== null) {
    const intent = look.act(sense);
    if (intent.lookAt !== undefined) self.lookAt = intent.lookAt;
  }

  /* 🎯 Nowhere to go: think of somewhere. ⚠️ **`null` back from the chooser is
     kept as `null`** — *"there is nowhere I can be"* is an honest answer and it
     looks like an animal pausing. It is never a reason to invent a destination. */
  if (self.goal === null && move !== null && !hold) {
    self.goal = chooseDestination(
      self,
      sense.ground,
      species.wander,
      species.place,
      species.score,
      sense.home ?? null,
      sense.pen ?? null,
    );
  }

  const stepped = stepBody(
    self.at,
    self.goal,
    self.heading,
    turn === 1 ? species.physique : { ...species.physique, turn: species.physique.turn * turn },
    pace,
    sense.ground,
    species.fits,
    dt,
  );
  self.at = stepped.at;
  self.heading = stepped.heading;

  /* 🔴 Give up on a goal we cannot reach, rather than grinding at a wall
     forever. ⚠️ Minecraft has no "path failed" branch at all — an animal that
     cannot get there keeps walking at the nearest point it found — so this is
     deliberately OUR behaviour and not theirs, and it exists because *an animal
     drifting hopefully at a wall* is the thing haugur would photograph. */
  if (stepped.blocked) self.goal = null;

  /* 🔴 THE NAME. It goes on the wire and into the F8 report. */
  self.moving = move?.name ?? null;
  self.looking = look?.name ?? null;
}
